import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { mainSiteUrl } from '@/mocks/categories';
import { faqItems, articlesContent } from '@/mocks/seoData';

const siteUrl = (import.meta.env as Record<string, string>).VITE_SITE_URL || 'https://basetaosheet.monster';

export default function ArticlePage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = slug ? articlesContent[slug] : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-accent-100 mx-auto mb-4">
              <i className="ri-file-unknow-line text-2xl text-accent-600"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">Article Not Found</h1>
            <p className="text-gray-600 text-sm mb-6">The article you are looking for does not exist or has been moved.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white font-semibold text-sm rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer">
              <i className="ri-home-4-line"></i>
              Back to Homepage
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleUrl = `${siteUrl}/article/${slug}`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.metaDescription,
    image: article.image,
    datePublished: '2026-08-10',
    dateModified: '2026-08-10',
    author: {
      '@type': 'Organization',
      name: 'BaseTao Spreadsheet Hub',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'BaseTao Spreadsheet Hub',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    about: {
      '@type': 'Thing',
      name: 'BaseTao Spreadsheet',
      description: 'A comprehensive research database for shoppers to track and compare products.',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Shopping Guide',
        item: `${siteUrl}/article/basetao-spreadsheet-guide`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.breadcrumbLabel,
        item: articleUrl,
      },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.slice(0, 6).map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <Navbar />
      <main className="pt-20">
        <article className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-gray-600 mb-8 flex-wrap" aria-label="breadcrumb">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap font-medium">Home</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to="/article/basetao-spreadsheet-guide" className="hover:text-primary-600 transition-colors font-medium">Shopping Guide</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-gray-900 font-semibold">{article.breadcrumbLabel}</span>
            </nav>

            <header className="mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold mb-4">
                <i className="ri-book-open-line"></i>
                <span>{article.badge}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-sm text-gray-600 mb-3">{article.subtitle}</p>
              <p className="text-sm text-gray-500 font-medium">
                Published: {article.publishedDate} &middot; {article.readingTime} &middot; Updated regularly
              </p>
            </header>

            <div className="w-full mb-10 rounded-2xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full object-cover"
                style={{ aspectRatio: '2/1' }}
              />
            </div>

            <div className="prose-content space-y-6 text-sm md:text-base text-gray-700 leading-relaxed">
              {article.sections.map((section, sIdx) => (
                <section key={sIdx}>
                  <h2 className="font-heading text-xl md:text-2xl font-semibold text-gray-900 mt-10 mb-4">
                    {section.heading}
                  </h2>
                  {section.content.map((para, pIdx) => (
                    <p key={pIdx} className="mb-4">
                      {para}
                    </p>
                  ))}
                </section>
              ))}
            </div>

            <div className="mt-12 p-6 rounded-2xl bg-primary-50 border border-primary-200">
              <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">{article.ctaTitle}</h3>
              <p className="text-sm text-gray-600 mb-4">{article.ctaText}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-primary-500 text-white font-semibold text-sm rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer shadow-sm"
                >
                  Visit Main Website
                  <i className="ri-arrow-right-up-line"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 px-6 py-2.5 border border-gray-300 text-gray-700 font-semibold text-sm rounded-full hover:border-gray-400 hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-4-line"></i>
                  Back to Homepage
                </Link>
              </div>
            </div>

            <div className="mt-10 border-t border-gray-200 pt-10">
              <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4">Related Resources</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {article.relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="p-4 rounded-xl bg-white border border-gray-200 hover:border-accent-300 hover:shadow-sm transition-all cursor-pointer"
                  >
                    <h4 className="text-sm font-semibold text-gray-800 mb-1">{related.title}</h4>
                    <p className="text-xs text-gray-500">{related.description}</p>
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-12">
              <h3 className="font-heading text-lg font-semibold text-gray-900 mb-5">Frequently Asked Questions</h3>
              <div className="space-y-3">
                {faqItems.slice(0, 6).map((item, idx) => (
                  <div key={idx} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                    <button
                      onClick={() => setOpenFAQ(openFAQ === idx ? null : idx)}
                      className="w-full flex items-center justify-between px-5 py-3.5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <h4 className="text-sm font-semibold text-gray-800 pr-4">{item.question}</h4>
                      <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${openFAQ === idx ? 'rotate-180' : ''}`}>
                        <i className="ri-arrow-down-s-line text-gray-500"></i>
                      </div>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${openFAQ === idx ? 'max-h-96 px-5 pb-4' : 'max-h-0'}`}>
                      <p className="text-xs text-gray-600 leading-relaxed">{item.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}